import { useMemo, useEffect, useCallback, useRef } from 'react'
import { useCore } from '../../hooks'
import { removeEmptyProperties } from '..'
import { FilterConfig, useFilters } from './useFilters'
import { useBasicEntities } from '../../hooks/useBasicEntities'

// Store for preserving group filter state across navigation
const groupFilterStateStore = {
  sort: null as string | null,
  tagIds: null as number[] | null,
}

export function useGroupFilters() {
  const { childCompanyId: coreChildCompanyId, setSelectedChildCompany: coreSetSelectedChildCompany } = useCore()
  const { tags } = useBasicEntities()

  // Find the tag with externalId === 'same'
  const defaultTag = useMemo(() => {
    return tags.find(tag => tag.externalId === 'same' && tag.type === 'order_group')
  }, [tags])

  // Initialize store with default tag if not set
  const isInitialized = useRef(false)
  useEffect(() => {
    if (!isInitialized.current && defaultTag && groupFilterStateStore.tagIds === null) {
      groupFilterStateStore.tagIds = [defaultTag.id]
      isInitialized.current = true
    }
  }, [defaultTag])

  const filterConfig: FilterConfig = useMemo(
    () => ({
      childCompanyId: {
        type: 'string',
        coreState: coreChildCompanyId,
        coreSetter: coreSetSelectedChildCompany,
        default: 'all',
      },
      // tagIds without coreState/coreSetter to prevent useFilters from resetting it
      tagIds: {
        type: 'array',
        default: [],
      },
      sort: { type: 'string', default: 'ordersCount_asc' },
    }),
    [coreChildCompanyId, coreSetSelectedChildCompany],
  )

  const { values, setFilter, resetFilters, isFilterRoute } = useFilters(filterConfig, '/groups')

  const { tagIds: urlTagIds = [], childCompanyId: selectedChildCompanyId, sort: sortParam } = values

  // Persist tagIds across navigations, prioritizing URL parameter
  // Fallback order: URL params -> stored value -> default tag
  const effectiveTagIds = useMemo(() => {
    if (urlTagIds.length > 0) {
      return urlTagIds
    }
    if (groupFilterStateStore.tagIds && groupFilterStateStore.tagIds.length > 0) {
      return groupFilterStateStore.tagIds
    }
    if (defaultTag) {
      return [defaultTag.id]
    }
    return []
  }, [urlTagIds, defaultTag])

  // Store tagIds when they come from URL, and restore when returning to route without URL params
  useEffect(() => {
    if (urlTagIds.length > 0) {
      // Update store when URL has tags
      groupFilterStateStore.tagIds = urlTagIds
    } else if (isFilterRoute && effectiveTagIds.length > 0) {
      // Restore tags to URL when returning to route without params
      setFilter('tagIds', effectiveTagIds)
    }
  }, [urlTagIds, effectiveTagIds, isFilterRoute, setFilter])

  const selectedTagIds = effectiveTagIds

  // Persist sort across navigations, prioritizing URL parameter
  const effectiveSortParam = sortParam ?? groupFilterStateStore.sort
  useEffect(() => {
    if (sortParam) {
      groupFilterStateStore.sort = sortParam
    } else if (effectiveSortParam && isFilterRoute) {
      // Restore sort from store when returning to filter route
      setFilter('sort', effectiveSortParam)
    }
  }, [sortParam, effectiveSortParam, isFilterRoute, setFilter])

  // Parse sort parameter from URL (format: field_direction, e.g., "ordersCount_desc")
  const { sort, direction } = useMemo(() => {
    if (effectiveSortParam) {
      const [field, dir] = effectiveSortParam.split('_')
      if (field && dir) {
        return {
          sort: field,
          direction: dir.toUpperCase(),
        }
      }
    }
    // Default sorting: ordersCount ASC
    return { sort: 'ordersCount', direction: 'ASC' }
  }, [effectiveSortParam])

  const setSelectedTagIds = (ids: number[]) => {
    groupFilterStateStore.tagIds = ids
    setFilter('tagIds', ids)
  }
  const setSelectedChildCompany = (value: string | undefined) =>
    setFilter('childCompanyId', value === 'all' ? undefined : value)
  const setSort = useCallback(
    (field: string, dir: 'ASC' | 'DESC') => {
      setFilter('sort', `${field}_${dir.toLowerCase()}`)
    },
    [setFilter],
  )

  const resetFiltersWithSort = useCallback(() => {
    groupFilterStateStore.sort = null
    groupFilterStateStore.tagIds = defaultTag ? [defaultTag.id] : null
    resetFilters()
  }, [resetFilters, defaultTag])

  const filters = useMemo(() => {
    return removeEmptyProperties({
      companies:
        selectedChildCompanyId && selectedChildCompanyId !== 'all' ? [Number(selectedChildCompanyId)] : undefined,
      tags: selectedTagIds.length > 0 ? selectedTagIds : undefined,
    })
  }, [selectedChildCompanyId, selectedTagIds])

  return {
    selectedTagIds,
    setSelectedTagIds,
    selectedChildCompanyId,
    setSelectedChildCompany,
    filters,
    resetFilters: resetFiltersWithSort,
    sort,
    direction,
    setSort,
  }
}
